/**
 * Shopify Native Analytics Platform
 *
 * Sends page view and ecommerce events to Shopify's monorail endpoint
 * so headless storefront traffic appears in Shopify Admin > Analytics.
 *
 * Uses `sendShopifyAnalytics` from `@shopify/hydrogen-react` which POSTs
 * to monorail-edge.shopifysvc.com/unstable/produce_batch with two schemas:
 *   - trekkie_storefront_page_view/1.4
 *   - custom_storefront_customer_tracking/1.2
 */

import type {
  ShopifyPageViewPayload,
  ShopifyAddToCartPayload,
} from '@shopify/hydrogen-react';

/**
 * Lazily load @shopify/hydrogen-react so the main SDK bundle never statically
 * requires it. It is an OPTIONAL peer dependency — only consumers that enable
 * Shopify-native analytics (the `shopifyNativeAnalytics` option) need it
 * installed. The module is imported once and cached. Every sender below is
 * fire-and-forget, so the async load boundary is invisible to callers.
 */
type HydrogenModule = typeof import('@shopify/hydrogen-react');
let _hydrogenPromise: Promise<HydrogenModule> | null = null;
function loadHydrogen(): Promise<HydrogenModule> {
  if (!_hydrogenPromise) {
    _hydrogenPromise = import('@shopify/hydrogen-react');
  }
  return _hydrogenPromise;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ShopifyNativeConfig {
  /** Numeric Shopify Shop ID (e.g. "1234567890") */
  shopId: string;
  /** Currency code (e.g. "USD") */
  currency: string;
  /** Shopify-accepted language code (e.g. "EN") */
  acceptedLanguage: string;
  /** Whether the user has consented to analytics tracking */
  hasUserConsent: boolean;
  /**
   * Headless channel storefront ID (PUBLIC_STOREFRONT_ID in Hydrogen).
   * Maps to `hydrogenSubchannelId` in Shopify's monorail schemas.
   * Required for session attribution in Shopify Admin > Analytics.
   * Found in Shopify Admin > Settings > Apps > Headless channel.
   */
  storefrontId?: string;
  /** Shopify store domain (e.g. "your-store.myshopify.com") for monorail routing */
  shopDomain?: string;
  /** Whether the user has consented to analytics tracking */
  analyticsAllowed?: boolean;
  /** Whether the user has consented to marketing tracking */
  marketingAllowed?: boolean;
  /**
   * Whether GDPR enforcement applies to this visitor (geography-driven, not
   * consent-driven). The host app is responsible for supplying this per
   * jurisdiction; the SDK does not geo-detect. Defaults to privacy-safe `true`.
   */
  gdprEnforced?: boolean;
  /**
   * Whether CCPA enforcement applies to this visitor. Host-app responsibility
   * per jurisdiction. Defaults to privacy-safe `true`.
   */
  ccpaEnforced?: boolean;
  /**
   * Whether sale of data is allowed for this visitor. Defaults to the
   * privacy-safe value `false`.
   */
  saleOfDataAllowed?: boolean;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/** Extract numeric ID string from a Shopify GID (e.g. gid://shopify/Product/123 → "123") */
function extractGidNumber(gid: string): string {
  return gid.match(/\/(\d+)$/)?.[1] || '0';
}

function buildBasePayload(h: HydrogenModule, config: ShopifyNativeConfig) {
  const analyticsAllowed = config.analyticsAllowed ?? config.hasUserConsent;
  const marketingAllowed = config.marketingAllowed ?? false;
  return {
    ...h.getClientBrowserParameters(),
    hasUserConsent: config.hasUserConsent,
    // Use "hydrogen" channel — Shopify's admin analytics pipeline processes
    // appClientId 6167201 (hydrogen) for session counting. The "headless"
    // channel (12875497473) may not have the same processing pipeline.
    shopifySalesChannel: h.ShopifySalesChannel.hydrogen,
    shopId: `gid://shopify/Shop/${config.shopId}`,
    currency: config.currency as ShopifyPageViewPayload['currency'],
    acceptedLanguage: config.acceptedLanguage as ShopifyPageViewPayload['acceptedLanguage'],
    ...(config.storefrontId && { storefrontId: config.storefrontId }),
    analyticsAllowed,
    marketingAllowed,
    // Compliance enforcement flags — geography-driven, not consent-driven.
    // Defaults are privacy-safe (enforce); the host app should pass explicit
    // values per visitor jurisdiction (the SDK does not geo-detect).
    saleOfDataAllowed: config.saleOfDataAllowed ?? false,
    ccpaEnforced: config.ccpaEnforced ?? true,
    gdprEnforced: config.gdprEnforced ?? true,
  };
}

// ---------------------------------------------------------------------------
// Event Senders
// ---------------------------------------------------------------------------

/**
 * Send a page view event to Shopify Analytics.
 * Called on every SPA navigation (piggybacks on pushGTMPageInfo).
 *
 * Uses PAGE_VIEW_2 (not PAGE_VIEW) to match Hydrogen's behavior:
 * PAGE_VIEW_2 sends trekkie + only `page_rendered` in the custom schema.
 * PAGE_VIEW sends trekkie + `page_rendered` + type-specific sub-events,
 * which duplicates the events sent by PRODUCT_VIEW/COLLECTION_VIEW/SEARCH_VIEW.
 */
export function shopifyPageView(
  pageType: string,
  config: ShopifyNativeConfig
): void {
  void (async () => {
    try {
      const h = await loadHydrogen();
      const payload: ShopifyPageViewPayload = {
        ...buildBasePayload(h, config),
        pageType,
      };
      h.sendShopifyAnalytics({
        eventName: h.AnalyticsEventName.PAGE_VIEW_2,
        payload,
      }, config.shopDomain || undefined);
    } catch {
      // Analytics must never throw
    }
  })();
}

/**
 * Send a product view event to Shopify Analytics.
 */
export interface ShopifyProductViewParams {
  productGid: string;
  productName: string;
  productBrand: string;
  variantGid: string;
  variantPrice: string;
  config: ShopifyNativeConfig;
  variantSku?: string;
  productCategory?: string;
  variantTitle?: string;
}

export function shopifyProductView(params: ShopifyProductViewParams): void {
  const {
    productGid, productName, productBrand, variantGid,
    variantPrice, config, variantSku, productCategory, variantTitle,
  } = params;
  void (async () => {
    try {
      const h = await loadHydrogen();
      const payload: ShopifyPageViewPayload = {
        ...buildBasePayload(h, config),
        pageType: 'product',
        resourceId: extractGidNumber(productGid),
        products: [
          {
            productGid,
            name: productName,
            brand: productBrand,
            variantGid,
            price: variantPrice,
            ...(variantSku && { sku: variantSku }),
            ...(productCategory && { category: productCategory }),
            ...(variantTitle && { variantName: variantTitle }),
          },
        ],
      };
      h.sendShopifyAnalytics({
        eventName: h.AnalyticsEventName.PRODUCT_VIEW,
        payload,
      }, config.shopDomain || undefined);
    } catch {
      // Analytics must never throw
    }
  })();
}

/**
 * Send a collection view event to Shopify Analytics.
 */
export function shopifyCollectionView(
  collectionGid: string,
  collectionHandle: string,
  config: ShopifyNativeConfig
): void {
  void (async () => {
    try {
      const h = await loadHydrogen();
      const payload: ShopifyPageViewPayload = {
        ...buildBasePayload(h, config),
        pageType: 'collection',
        resourceId: extractGidNumber(collectionGid),
        collectionHandle,
      };
      h.sendShopifyAnalytics({
        eventName: h.AnalyticsEventName.COLLECTION_VIEW,
        payload,
      }, config.shopDomain || undefined);
    } catch {
      // Analytics must never throw
    }
  })();
}

/**
 * Send a search view event to Shopify Analytics.
 */
export function shopifySearchView(
  searchString: string,
  config: ShopifyNativeConfig
): void {
  void (async () => {
    try {
      const h = await loadHydrogen();
      const payload: ShopifyPageViewPayload = {
        ...buildBasePayload(h, config),
        pageType: 'search',
        searchString,
      };
      h.sendShopifyAnalytics({
        eventName: h.AnalyticsEventName.SEARCH_VIEW,
        payload,
      }, config.shopDomain || undefined);
    } catch {
      // Analytics must never throw
    }
  })();
}

/**
 * Send an add-to-cart event to Shopify Analytics.
 */
export function shopifyAddToCart(
  cartId: string,
  totalValue: number,
  products: Array<{
    productGid: string;
    variantGid: string;
    name: string;
    brand: string;
    price: string;
    quantity: number;
    sku?: string;
    category?: string;
    variantName?: string;
  }>,
  config: ShopifyNativeConfig
): void {
  void (async () => {
    try {
      const h = await loadHydrogen();
      const payload: ShopifyAddToCartPayload = {
        ...buildBasePayload(h, config),
        cartId,
        totalValue,
        products: products.map((p) => ({
          productGid: p.productGid,
          variantGid: p.variantGid,
          name: p.name,
          brand: p.brand,
          price: p.price,
          quantity: p.quantity,
          ...(p.sku && { sku: p.sku }),
          ...(p.category && { category: p.category }),
          ...(p.variantName && { variantName: p.variantName }),
        })),
      };
      h.sendShopifyAnalytics({
        eventName: h.AnalyticsEventName.ADD_TO_CART,
        payload,
      }, config.shopDomain || undefined);
    } catch {
      // Analytics must never throw
    }
  })();
}
